import Wrapper, { wrapperTypes } from 'global/wrappers/Wrapper'
import _ from 'lodash'
import { _seedSortTypes } from 'projectSpecific/sports/useHelpers/usePlayoffInfo'

const SeedingsLegend = ({ sortType }) => {

  const content_sortType = () => {
    switch (sortType) {
      case _seedSortTypes.elo:
        return content_elo()
      case _seedSortTypes.rpi:
        return content_rpi()
      case _seedSortTypes.ppm:
        return content_ppm()
      case _seedSortTypes.owp:
        return content_owp()
      case _seedSortTypes.sectionRank:
        return content_sectionRank()
      case _seedSortTypes.sos:
        return content_sos()
      case _seedSortTypes.pd:
        return content_pd()
      // case _seedSortTypes.ew:
      //   return content_pd()
      default:
        return 'asdfasfdasfdasdf'
    }
  }

  const content = () => <Wrapper
    header={_.startCase(sortType)}
    content={content_sortType()}
    wrapperType={wrapperTypes.padded}
  >
  </Wrapper>

  return content()
}

export default SeedingsLegend

const content_elo = () => (
  <div style={{ display: 'flex', gap: '.5em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the Elo System Works</h4>
    <div>
      <span className='highlight'>Teams start with a rating</span> (default 1500, adjusted slightly by section if needed)
    </div>

    <div>
      <span className='highlight'>Matches are processed in chronological order</span> so ratings evolve over the season naturally
    </div>

    <div>
      <span className='highlight'>Expected result is calculated</span> using the rating difference between the two teams
    </div>

    <div>
      <span className='highlight'>Actual result is binary</span> (win = 1, loss = 0) based on set score (home vs away)
    </div>

    <div>
      <span className='highlight'>Margin of victory matters</span> using set difference (e.g. 3–0 has more impact than 3–2)
    </div>

    <div>
      <span className='highlight'>Ratings are updated after every match</span> using:
      <br />
      <div>New Rating = Old Rating + K × (Actual - Expected) × Multiplier</div>
    </div>

    <div>
      <span className='highlight'>K-factor changes over time</span> (higher early season, lower later) to stabilize rankings
    </div>

    <div>
      <span className='highlight'>Home advantage is applied</span> when calculating expected outcomes (home teams get a small rating boost)
    </div>

    <div>
      <span className='highlight'>Group (Section + Level) strength is applied</span> by adding a dynamic boost based on how each group performs against other groups:
      <br />
      <div>This boost increases expectations for stronger groups and lowers them for weaker groups, based on real results</div>
    </div>

    <div>
      <span className='highlight'>Final result is a dynamic ranking</span> that reflects strength, schedule difficulty, and performance over time
    </div>
  </div>)

const content_rpi = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the RPI System Works</h4>
    <div>
      <span className='highlight'>Based on win percentage</span> — teams are evaluated primarily by wins and losses
    </div>

    <div>
      <span className='highlight'>Winning Percentage (WP)</span> = your wins ÷ total games played
    </div>

    <div>
      <span className='highlight'>Opponent Winning Percentage (OWP)</span> = average win % of your opponents (excluding games vs you)
    </div>

    <div>
      <span className='highlight'>Opponent’s Opponent Winning Percentage (OOWP)</span> = average win % of your opponents’ opponents
    </div>

    <div>
      <span className='highlight'>Final RPI formula:</span>
      <br />
      <code>RPI = (0.25 × WP) + (0.50 × OWP) + (0.25 × OOWP)</code>
    </div>

    <div>
      <span className='highlight'>Strength of schedule matters</span> — playing strong teams improves your rating
    </div>

    <div>
      <span className='highlight'>All games are weighted equally</span> — margin of victory and timing do not matter
    </div>

    <div>
      <span className='highlight'>No game order impact</span> — early and late season results are treated the same
    </div>

    <div>
      <span className='highlight'>Final result is a resume-based ranking</span> focused on record and opponent strength
    </div>
  </div>

const content_ppm = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the PPM System Works</h4>
    <div>
      <span className='highlight'>Based on Points Per Match</span>
    </div>

    <div>
      3 Points for a 3-0 or 3-1 win
    </div>
    <div>
      2 points for a 3-2 win
    </div>
    <div>
      1 point for a 3-2 loss.
    </div>
  </div>

const content_owp = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the OWP System Works</h4>
    <div>
      <span className='highlight'>Based on wins / matches played</span>
    </div>
  </div>

const content_sectionRank = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the Section Rank System Works</h4>
    <div>
      <span className='highlight'>Based section rankings</span>
    </div>
    <div>
      <span className='highlight'>All top ranked teams will be listed first</span>
    </div>
  </div>

const content_sos = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the Strength of Schedule System Works</h4>
    <div>
      <span className='highlight'>Average Elo of all opponents played</span>
    </div>
    <div>
      <span className='highlight'>Higher = tougher competition</span>
    </div>
  </div>

const content_pd = () =>
  <div style={{ display: 'flex', gap: '1em', flexDirection: 'column', padding: '1em' }} >
    <h4>How the Performance Delta System Works</h4>
    <div>
      <span className='highlight'>Expected Wins</span>: How many games the team was expected to win based on Elo probabilities
    </div>
    <div>
      <span className='highlight'>Acutal Wins</span>: How many games the team actually won
    </div>
    <div>
      <span className='highlight'>Performance Delta</span>: actualWins - expectedWins
    </div>
    <div>
      Positive = overperforming
    </div>
    <div>
      Negative = underperforming
    </div>
  </div>